import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AccountId,
  SecuritySymbol,
  SymbolType,
  AutoSuggestSymbol,
  SymbolLookupDataSource,
  MarketType,
  FractionalResponse,
  SecuritySymbolHelper,
  AutoSuggestFilterBy
} from '../../domain/public-api';
import { RestResponse } from '../../services/rest/rest.domain';
import { RestOptions } from '../../services/rest/rest-request';
import { RestService } from '../../services/rest/rest.service';

@Injectable({
  providedIn: 'root'
})
export class SymbolLookupService implements SymbolLookupDataSource {
  private static readonly DEFAULT_MAX_RESULT: number = 10;

  constructor(private restService: RestService) {
  }

  static symbolToString(item: AutoSuggestSymbol): string {
    if (item.symbolType === SymbolType.MUTUALFUND) {
      return item.symbol !== undefined ? item.symbol : '';
    }
    return item.market !== undefined ? `${item.symbol} ${item.market}` : '';
  }

  search<T extends AutoSuggestSymbol>(symbol: string, maxResults: number = 7,
                                      symbolTypes?: SymbolType | Array<SymbolType>, market?: MarketType,
                                      fractionalEligible?: boolean,
                                      filterBy?: AutoSuggestFilterBy): Observable<Array<T>> {
    if (fractionalEligible) {
      const restParams: RestOptions = {
        params: { symbol, marketType: market, pageNum: 0, pageSize: maxResults }
      } as RestOptions;
      return this.restService.getResource('security.discoverFractional')
        .get<FractionalResponse<T>>(restParams)
        .pipe(
          map((data: FractionalResponse<T>): Array<T> => this.mapSymbols(data.fractionalList))
        );
    } else {
      const restOptions: RestOptions = {
        params: {
          term: symbol,
          maxResults
        }
      };
      if (symbolTypes !== undefined) {
        restOptions.params.types = symbolTypes;
      }
      if (market !== undefined) {
        restOptions.params.market = market;
      }
      if (filterBy !== undefined) {
        restOptions.params.filterBy = filterBy;
      }
      return this.restService.getResource('security.autosuggest')
        .get<Array<T>>(restOptions)
        .pipe(
          map((data: Array<T>): Array<T> => this.mapSymbols(data))
        );
    }
  }

  getRecentSymbols<T extends AutoSuggestSymbol>(maxResults: number = 5,
                                                symbolTypes?: SymbolType | Array<SymbolType>, market?: MarketType): Observable<Array<T>> {
    const restOptions: RestOptions = {
      params: {
        maxResults
      }
    };
    if (symbolTypes !== undefined) {
      restOptions.params.types = symbolTypes;
    }
    if (market !== undefined) {
      restOptions.params.market = market;
    }
    return this.restService.getResource('security.getrecentsearches')
      .get<Array<T>>(restOptions)
      .pipe(
        map((data: Array<T>): Array<T> => this.mapSymbols(data))
      );
  }

  saveRecentSymbols(symbol: AutoSuggestSymbol, symbolTypes?: SymbolType | Array<SymbolType>): Observable<RestResponse<unknown>> {
    const restOptions: RestOptions = {
      params: {
        symbol: symbol.symbol,
        market: symbol.market
      }
    };
    if (symbolTypes !== undefined) {
      restOptions.params.types = symbolTypes;
    }
    return this.restService.getResource('security.recentsearches').post({}, restOptions);
  }
  /**
   * Returns Observable<boolean>
   *
   * To check if the free form symbol text maps to a valid symbol.
   *
   * @param symbol
   * An example of freeFormSymbolText = 'TD CA'
   * @param symbolType
   * The symbol lookup widget changes the users text (string) to a symbol object.
   */

  isFreeFormSymbolValid(symbol: AutoSuggestSymbol, symbolType: SymbolType | Array<SymbolType>): Observable<boolean> {
    if (symbol !== undefined) {
      return this.search(symbol.symbol, SymbolLookupService.DEFAULT_MAX_RESULT, symbolType)
        .pipe(
          map((symbols: Array<AutoSuggestSymbol>): boolean => symbols
            .find((item: SecuritySymbol): boolean => SecuritySymbolHelper.equals(symbol, item)) !== undefined)
        );
    }
    return of(false);
  }

  queryPositionSymbols<T extends AutoSuggestSymbol>(accountId: AccountId,
                                                    symbolRequest: string,
                                                    maxResults: number,
                                                    transferToken: string,
                                                    contributionType: string): Observable<Array<T>> {
    const request = {
      term: symbolRequest,
      maxResults: maxResults === undefined ? SymbolLookupService.DEFAULT_MAX_RESULT : maxResults,
      transferToken,
      contributionTypeCd: contributionType
    };
    return this.restService.getResource('security.autosuggest')
      .get<Array<T>>({
        pathVariables: {
          accountId
        },
        params: request
      })
      .pipe(
        map((data: Array<T>): Array<T> => this.mapSymbols(data))
      );
  }

  private mapSymbols<T extends AutoSuggestSymbol>(data: Array<T>): Array<T> {
    data.forEach((item: T): void => {
      item.market = item.marketType ?? item.market;
      item.toString = (): string => SymbolLookupService.symbolToString(item);
    });
    return data;
  }
}
